/**
 * Specialty shell — the themed workspace frame.
 *
 * rev v11.0.0 · Phase 2 / C3
 *
 * The shell renders what the manifest declares and nothing else: the
 * navigation modules of the specialty, filtered by the caller's
 * capabilities, and the panels of the workspace.
 *
 * It knows no specialty by name. Adding a specialty is adding a manifest,
 * never editing this file.
 */
import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import {
  Activity,
  BadgeCheck,
  Brain,
  Camera,
  ClipboardCheck,
  Crosshair,
  Droplet,
  Ear,
  Eye,
  FileText,
  FlaskConical,
  Gauge,
  HeartPulse,
  History,
  Images,
  ListChecks,
  Pill,
  Receipt,
  Scan,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  Syringe,
  Users,
  Waves,
  Wind,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { specialtyRegistry, type NavModule } from "@dikdoc/specialty-registry";
import { moduleRoute } from "@/lib/specialty";
import { asRoutePath } from "@/lib/routes";

/** The lucide icons the manifests may refer to. */
const ICONS: Record<string, typeof Activity> = {
  Activity,
  BadgeCheck,
  Brain,
  Camera,
  ClipboardCheck,
  Crosshair,
  Droplet,
  Ear,
  Eye,
  FileText,
  FlaskConical,
  Gauge,
  HeartPulse,
  History,
  Images,
  ListChecks,
  Pill,
  Receipt,
  Scan,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  Syringe,
  Users,
  Waves,
  Wind,
};

/** The icon of a module, with a safe fallback. */
function Icon({ name, className }: { name: string; className?: string }) {
  const Cmp = ICONS[name] ?? Activity;
  return <Cmp className={className} />;
}

export interface SpecialtyShellProps {
  /** The canonical slug of the specialty. */
  specialtyId: string;
  /** The capabilities of the caller. A module whose capability is missing is hidden. */
  capabilities?: readonly string[];
  /** The panels of the workspace. */
  children?: ReactNode;
  /** The module currently displayed. */
  activeModule?: string;
}

export function SpecialtyShell({
  specialtyId,
  capabilities = [],
  children,
  activeModule,
}: SpecialtyShellProps) {
  const specialty = specialtyRegistry.get(specialtyId);
  if (!specialty) return null;

  const navigation = specialtyRegistry.navigation(specialtyId, capabilities);

  return (
    <div className="space-y-5 p-6" data-specialty={specialty.id}>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight">{specialty.label}</h1>
            <Badge variant="outline" className="text-[10px]">
              {specialty.short}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{specialty.scope}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-[10px]">
            {specialty.ngap.catalogueActs > 0
              ? `${specialty.ngap.catalogueActs} actes NGAP`
              : "Cotations dans un autre périmètre"}
          </Badge>
          <Badge variant="outline" className="text-[10px]">
            {specialty.featureFlag}
          </Badge>
        </div>
      </header>

      {navigation.length > 0 && (
        <nav aria-label={`Navigation ${specialty.label}`} className="flex flex-wrap items-center gap-2">
          {navigation.map((module) => (
            <ModuleLink
              key={module.id}
              module={module}
              active={activeModule === module.id}
            />
          ))}
        </nav>
      )}

      <Separator />

      {children}
    </div>
  );
}

function ModuleLink({ module, active }: { module: NavModule; active: boolean }) {
  const target = moduleRoute(module.route);
  // A module whose screen does not exist yet is DECLARED as such: it stays
  // visible, it says so in words, and it links nowhere. Linking to a route
  // that does not exist was worse than useless — the router matched it
  // against `/clinical/$specialty`, the specialty could not be resolved, and
  // the clinician was silently sent back to the home page.
  if (!target) {
    return (
      <Badge variant="secondary" className="gap-1 text-[10px]">
        <Icon name={module.icon} className="h-3 w-3" />
        {module.label}
        <span className="font-normal opacity-70">— pas encore livré</span>
      </Badge>
    );
  }
  return (
    <Button
      variant={active ? "default" : "outline"}
      size="sm"
      className="gap-2"
      asChild
    >
      <Link to={asRoutePath(target.to)}>
        <Icon name={module.icon} className="h-3.5 w-3.5" />
        {module.label}
      </Link>
    </Button>
  );
}

/**
 * The panels of a specialty.
 *
 * The component key is resolved by the caller: the registry declares which
 * panel belongs to the workspace, the application decides which React
 * component implements it.
 */
export interface SpecialtyPanelProps {
  specialtyId: string;
  capabilities?: readonly string[];
  render: (component: string, panelId: string) => ReactNode;
}

export function SpecialtyPanels({
  specialtyId,
  capabilities = [],
  render,
}: SpecialtyPanelProps) {
  const panels = specialtyRegistry.panels(specialtyId, capabilities);
  if (panels.length === 0) return null;
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {panels.map((panel) => (
        <Card key={panel.id}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Icon name={panel.icon} className="h-4 w-4 text-primary" />
              {panel.label}
            </CardTitle>
            <CardDescription className="sr-only">{panel.id}</CardDescription>
          </CardHeader>
          <CardContent>{render(panel.component, panel.id)}</CardContent>
        </Card>
      ))}
    </div>
  );
}
